import { ExternalLink, Plus, Puzzle, Store } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  EmptyState,
  GlassCard,
  SettingsPageHeader,
  Pill,
  SurfacePageBody,
  SurfacePageLayout,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { UPLOAD_FOLDER_ICON } from '@/shared/upload-icons';
import {
  SEED_INSTALLED_PLUGINS,
  SEED_PLUGIN_MARKETPLACES,
  type InstalledPluginSeed,
  type PluginMarketplaceSeed,
} from '@/versions/v1/shared/settings-seed';

import { AddMarketplaceForm } from './AddMarketplaceForm';
import { InstalledPluginCard } from './InstalledPluginCard';

export function PluginsScene() {
  const [plugins, setPlugins] = useState<InstalledPluginSeed[]>(
    () => [...SEED_INSTALLED_PLUGINS],
  );
  const [marketplaces, setMarketplaces] = useState<PluginMarketplaceSeed[]>(
    () => [...SEED_PLUGIN_MARKETPLACES],
  );

  const sorted = useMemo(
    () => [...plugins].sort((a, b) => b.installedAt - a.installedAt),
    [plugins],
  );

  const handleUninstall = (id: string) => {
    setPlugins((prev) => prev.filter((entry) => entry.id !== id));
  };

  const handleAddMarketplace = (marketplace: PluginMarketplaceSeed) => {
    setMarketplaces((prev) => {
      if (prev.some((entry) => entry.id === marketplace.id)) return prev;
      return [...prev, marketplace];
    });
  };

  return (
    <SurfacePageLayout width="narrow">
      <Commentable name="PluginsScene">
        <SurfacePageBody className="space-y-8" as="div">
          <SettingsPageHeader
            title="Plugins"
            description="Bundles of skills, hooks, commands, and MCP servers the agent can load."
            actions={
              <>
                <button
                  type="button"
                  disabled
                  title="Upload plugin is not available in this mock"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-400 opacity-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-400"
                >
                  <UPLOAD_FOLDER_ICON className="h-4 w-4" />
                  Upload plugin
                </button>
                <Link
                  to="/settings/plugins/marketplace"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-accent-500 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-600 dark:bg-accent-500 dark:text-white dark:hover:bg-accent-400"
                >
                  <Plus className="h-4 w-4" />
                  Browse marketplace
                </Link>
              </>
            }
          />

          <section>
            <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted">
              Installed
            </h2>
            {sorted.length === 0 ? (
              <EmptyState
                icon={<Puzzle className="h-8 w-8 text-zinc-400" />}
                title="No plugins installed yet"
                description="Browse the marketplace to install your first plugin."
                action={
                  <Link
                    to="/settings/plugins/marketplace"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:hover:bg-zinc-800"
                  >
                    <Plus className="h-4 w-4" />
                    Browse marketplace
                  </Link>
                }
              />
            ) : (
              <GlassCard className="overflow-hidden">
                {sorted.map((plugin, index) => (
                  <InstalledPluginCard
                    key={plugin.id}
                    plugin={plugin}
                    bordered={index > 0}
                    onUninstall={handleUninstall}
                  />
                ))}
              </GlassCard>
            )}
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-medium uppercase tracking-wide text-muted">
                Marketplaces
              </h2>
            </div>

            {marketplaces.length === 0 ? (
              <EmptyState
                icon={<Store className="h-8 w-8 text-zinc-400" />}
                title="No marketplaces"
                description="The default marketplace should auto-seed — try refreshing."
              />
            ) : (
              <GlassCard className="overflow-hidden">
                {marketplaces.map((marketplace, index) => (
                  <div
                    key={marketplace.id}
                    className={`flex items-center gap-4 px-5 py-4 ${
                      index > 0 ? 'border-t border-[var(--glass-border)]' : ''
                    }`}
                  >
                    <Store className="h-4 w-4 flex-shrink-0 text-zinc-400 dark:text-zinc-500" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">
                          {marketplace.name ?? marketplace.id}
                        </span>
                        <Pill>{marketplace.source}</Pill>
                      </div>
                      <a
                        href={marketplace.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 inline-flex items-center gap-1 truncate text-xs text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"
                      >
                        {marketplace.url}
                        <ExternalLink className="h-3 w-3 flex-shrink-0" />
                      </a>
                    </div>
                    <Link
                      to={`/settings/plugins/marketplace?source=${encodeURIComponent(marketplace.id)}`}
                      className="flex-shrink-0 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 transition-colors hover:bg-zinc-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-200 dark:hover:bg-zinc-800"
                    >
                      Browse
                    </Link>
                  </div>
                ))}
              </GlassCard>
            )}

            <div className="mt-4">
              <AddMarketplaceForm onAdd={handleAddMarketplace} />
            </div>
          </section>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
